import { Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  Bell,
  HelpCircle,
  LogOut,
  Menu,
  X,
  Check,
  WifiOff,
  RefreshCw,
  Leaf,
  LoaderCircle,
  Store,
  Building2,
  Eye,
  UserRound,
} from "lucide-react";
import {
  api,
  ApiError,
  type Snapshot,
  type Row,
  loadCore,
  invalidate,
  invalidateLater,
  clearClientCache,
  patchQuery,
  patchQueries,
  mergeCountChanges,
  setCurrentVenue,
} from "./api";
import {
  cacheWorkspace,
  offlineWorkspace,
  clearOffline,
  pending,
  scopeOf,
  syncPending,
} from "./offline";
import { Badge, Modal, type PageProps } from "./ui";
import { featurePages } from "./features";
import { GlobalSearch } from "./features/search/GlobalSearch";
import { useHardwareScanner } from "./scanner/useHardwareScanner";
import { Mark, Wordmark } from "./brand";
import { pageAllowed } from "./features/team/pageAccess";
import { SideNav } from "./nav/SideNav";
import { buildMenu, navBadges, pageTitle, searchablePages } from "./nav/menu";
import { SAVED_EVENT, UNDOABLE } from "./features/staff/undoEvents";
import { lazyPage, ScreenErrorBoundary } from "./errors";
import { signOut } from "./signOut";
import { Toast, useToastEvents } from "./ui/Toast";
import { confirmLeave } from "./ui/useUnsavedChanges";
import { ShortcutsHost } from "./features/shortcuts/ShortcutsHost";
import { RecordHost } from "./features/history/RecordHost";
// Loaded only when needed, so the app shell stays small (docs/features/performance.md).
const Auth = lazyPage(() =>
  import("./AuthScreen").then((m) => ({ default: m.Auth })),
);
// Sam, the in-app assistant, floats on every signed-in page (the public helpers stay on the homepage).
const SamLauncher = lazyPage(() =>
  import("./features/assistant/SamLauncher").then((m) => ({
    default: m.SamLauncher,
  })),
);
const AccessBanner = lazyPage(() =>
  import("./features/account/AccessBanner").then((m) => ({
    default: m.AccessBanner,
  })),
);
// "How this page works" (docs/features/help.md): loaded when the ? is first pressed.
const HelpDrawer = lazyPage(() =>
  import("./features/help/HelpDrawer").then((m) => ({ default: m.HelpDrawer })),
);
const UndoBar = lazyPage(() =>
  import("./features/staff/UndoBar").then((m) => ({ default: m.UndoBar })),
);
// Each page's code loads when it is first opened, so the initial bundle
// stays small however many features exist.
const Today = lazyPage(() =>
  import("./features/today/Today").then((m) => ({ default: m.Today })),
);
// Simple mode (staff): four big buttons instead of Today (docs/features/operator-ux.md).
const StaffHome = lazyPage(() =>
  import("./features/staff/StaffHome").then((m) => ({ default: m.StaffHome })),
);
const Inventory = lazyPage(() =>
  import("./Inventory").then((m) => ({ default: m.Inventory })),
);
const Counts = lazyPage(() =>
  import("./Inventory").then((m) => ({ default: m.Counts })),
);
const Purchasing = lazyPage(() =>
  import("./Pages").then((m) => ({ default: m.Purchasing })),
);
const Recipes = lazyPage(() =>
  import("./features/recipes/RecipesPage").then((m) => ({ default: m.RecipesPage })),
);
const Connections = lazyPage(() =>
  import("./Pages").then((m) => ({ default: m.Connections })),
);
const Reports = lazyPage(() =>
  import("./Pages").then((m) => ({ default: m.Reports })),
);
const Service = lazyPage(() =>
  import("./features/service/ServicePage").then((m) => ({
    default: m.ServicePage,
  })),
);
const SettingsPage = lazyPage(() =>
  import("./Pages").then((m) => ({ default: m.SettingsPage })),
);
const Team = lazyPage(() =>
  import("./Pages").then((m) => ({ default: m.Team })),
);
const PageLoading = () => (
  <div className="boot page-loading">
    <LoaderCircle className="spin" />
  </div>
);
/**
 * Endpoints whose reply is the changed core record: the screen updates that
 * record in place instead of reloading the workspace.
 */
const IN_PLACE: [
  RegExp,
  (
    | string
    | ((s: Snapshot, value: any, match: RegExpExecArray) => Snapshot | null)
  ),
][] = [
  [/^\/products(\/[^/]+)?$/, "products"],
  [/^\/areas$/, "areas"],
  // Recipes and orders are not in the core (the core's recipe counts and
  // open-order headers reload; the lists refetch through their queries).
  [/^\/visits(\/[^/]+)?$/, "visits"],
  [/^\/containers(\/[^/]+)?$/, "containers"],
  // A renamed shelf (shelf builder).
  [/^\/counting\/shelves\/[^/]+$/, "shelves"],
  // A count entry: the saved observation goes into the open count's detail
  // and its header; only that count's queries refetch (see countEntrySaved).
  [/^\/counts\/([^/]+)\/observations$/, applyCountEntry],
];
/**
 * PUT /counts/:id/observations returns the saved observation. Patch the
 * count detail (useCountDetail) and the header in the core: a new product/area
 * pair adds one to observationCount, and the count returns to draft (a
 * requested recount during review keeps it in review), as the server does.
 */
function applyCountEntry(
  s: Snapshot,
  value: any,
  match: RegExpExecArray,
): Snapshot | null {
  const countId = match[1];
  if (
    !value ||
    typeof value.id !== "string" ||
    !s.counts.some((c) => c.id === countId)
  )
    return null;
  // A completed recount changes recount records too: reload the core instead.
  if (value.recountId) return null;
  patchQuery(`/counts/${countId}`, (detail: Row) =>
    mergeCountChanges(detail, { observations: [value] }),
  );
  return {
    ...s,
    counts: s.counts.map((c) =>
      c.id === countId
        ? {
            ...c,
            observationCount:
              (Number(c.observationCount) || 0) + (value.version === 1 ? 1 : 0),
            status: "draft",
          }
        : c,
    ),
  };
}
/** After a count entry: refresh that count's progress, detail (delta) and, debounced, its sheets. */
function countEntrySaved(
  countId: string,
  setS: (fn: (s: Snapshot | null) => Snapshot | null) => void,
) {
  api<Row>(`/counting/counts/${countId}/progress`)
    .then((progress) =>
      setS((prev) =>
        prev
          ? {
              ...prev,
              countProgress: [
                ...(prev.countProgress ?? []).filter(
                  (x: Row) => x.countId !== countId,
                ),
                progress,
              ],
            }
          : prev,
      ),
    )
    .catch(() => undefined);
  invalidate([`/counts/${countId}`]);
  invalidateLater([`/counting/counts/${countId}/`], 1500);
}
/** A placement the server saved, applied to one GET /counting/placements reply (it goes last on its shelf, as on the server). */
function placeInQuery(data: Row, queryPath: string, pl: Row): Row {
  const shelves: Row[] = data?.shelves ?? [];
  const covers = shelves.some((x) => x.areaId === pl.areaId) || !queryPath.includes("areaIds=") || decodeURIComponent(queryPath).includes(pl.areaId);
  if (!covers) return data;
  const next = shelves.map((x) =>
    x.areaId !== pl.areaId ? x : { ...x, productIds: (x.productIds ?? []).filter((id: string) => id !== pl.productId) },
  );
  const target = next.find((x) => x.id === pl.shelfId);
  if (target) target.productIds = [...target.productIds, pl.productId];
  else next.push({ id: pl.shelfId, areaId: pl.areaId, productIds: [pl.productId] });
  return { ...data, shelves: next };
}
function applyInPlace(s: Snapshot, path: string, value: any): Snapshot | null {
  if (
    path === "/workspace" &&
    value &&
    typeof value === "object" &&
    value.id === s.workspace.id
  ) {
    // The venue fields on Settings describe the current venue too.
    const venue = s.venue
      ? {
          ...s.venue,
          name: value.venueName ?? s.venue.name,
          city: value.city ?? s.venue.city,
          venueType: value.venueType ?? s.venue.venueType,
        }
      : s.venue;
    return {
      ...s,
      workspace: value,
      venue,
      venues:
        s.venues?.map((v) => (v.id === venue?.id ? venue : v)) ?? s.venues,
      company: s.company
        ? {
            ...s.company,
            name: value.name ?? s.company.name,
            productApproval: value.productApproval === true,
          }
        : s.company,
    };
  }
  // The shelf builder and "put it on this shelf" (POST /counting/areas/:id/place):
  // the area's live shelves in the core, and the one placement that changed in
  // every mounted placements query (they also refetch after the save).
  if (/^\/counting\/areas\/[^/]+\/place$/.test(path) && value && Array.isArray(value.shelves)) {
    const pl = value.placement;
    if (pl && value.changed !== false) patchQueries("/counting/placements", (data: Row, queryPath) => placeInQuery(data, queryPath, pl));
    return {
      ...s,
      shelves: [...(s.shelves ?? []).filter((x: Row) => x.areaId !== value.areaId), ...value.shelves],
    };
  }
  const match = IN_PLACE.find(([re]) => re.test(path));
  if (!match || !value || typeof value.id !== "string") return null;
  if (typeof match[1] === "function")
    return match[1](s, value, match[0].exec(path)!);
  const key = match[1],
    list: Row[] = s[key] ?? [];
  const next = list.some((x) => x.id === value.id)
    ? list.map((x) => (x.id === value.id ? { ...x, ...value } : x))
    : [...list, value];
  return { ...s, [key]: next };
}
export default function App() {
  const [s, setS] = useState<Snapshot | null>(null),
    [loading, setLoading] = useState(true),
    [loadError, setLoadError] = useState(""),
    [page, setPage] = useState(initialPage),
    [modal, setModal] = useState<ReactNode>(null),
    [toast, setToast] = useState<{ text: string; error: boolean } | null>(null),
    [navOpen, setNavOpen] = useState(false),
    [profileMenu, setProfileMenu] = useState(false),
    [offline, setOffline] = useState(!navigator.onLine),
    [queued, setQueued] = useState(0);
  // The profile menu closes whenever the page or the drawer changes.
  useEffect(() => setProfileMenu(false), [page, navOpen]);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notify = (text: string, error = false) => {
    setToast({ text, error });
    // A newer message gets its own full time on screen.
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), error ? 8000 : 5000);
  };
  // showToast() from components that have no notify prop (drawers, hooks).
  useToastEvents(notify);
  const reload = async () => {
    try {
      const value = await loadCore();
      setS(value);
      setLoadError("");
      setOffline(false);
      await cacheWorkspace(value);
      setQueued((await pending(scopeOf(value))).length);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setS(null);
        return;
      }
      throw e;
    }
  };
  useEffect(() => {
    reload()
      .catch(async (e) => {
        const cached = await offlineWorkspace();
        if (cached) {
          setS(cached);
          setOffline(true);
          setQueued((await pending(scopeOf(cached))).length);
        } else setLoadError(e.message);
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    const on = () => {
      setOffline(false);
      if (s)
        syncPending(s)
          .then(() => reload())
          .catch((e) => notify(e.message, true));
    };
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, [s?.workspace.id]);
  // Reminders are recomputed in the background after a change; while the
  // core says they are stale, look again shortly (a few times at most).
  const staleChecks = useRef(0);
  useEffect(() => {
    if (!s?.actionsStale) {
      staleChecks.current = 0;
      return;
    }
    if (staleChecks.current >= 5) return;
    const timer = setTimeout(() => {
      staleChecks.current += 1;
      reload().catch(() => undefined);
    }, 3000);
    return () => clearTimeout(timer);
  }, [s]);
  // Phone drawer: focus moves into it when it opens and back to the menu
  // button when it closes; Escape closes it.
  const menuButton = useRef<HTMLButtonElement>(null),
    drawerClose = useRef<HTMLButtonElement>(null),
    drawerWasOpen = useRef(false);
  useEffect(() => {
    if (navOpen) {
      drawerWasOpen.current = true;
      drawerClose.current?.focus();
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") setNavOpen(false);
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
    if (drawerWasOpen.current) {
      drawerWasOpen.current = false;
      // Only when the menu button is showing (phone widths) and nothing else took focus.
      if (
        menuButton.current?.offsetParent &&
        (!document.activeElement ||
          document.activeElement === document.body ||
          document.activeElement.closest(".sidebar"))
      )
        menuButton.current.focus();
    }
  }, [navOpen]);
  // Handheld barcode scanners (keyboard mode) anywhere in the signed-in app.
  useHardwareScanner(!!s);
  // The undo bar (and its code) loads with the first save it could reverse.
  const [undoReady, setUndoReady] = useState(false);
  const go = (p: string) => {
    // Unsaved edits on this page ask first (ui/useUnsavedChanges.ts).
    if (p !== page && !confirmLeave()) return;
    setPage(p);
    setNavOpen(false);
    window.scrollTo({ top: 0 });
  };
  /**
   * Sends a change, then applies the returned record in place when the
   * endpoint returns a core record; otherwise reloads the small core. Either
   * way every mounted history list refetches.
   */
  const mutate = async (path: string, body?: any, method?: string) => {
    const value = await api(path, body, method);
    // The undo bar listens for saves it can reverse.
    if (UNDOABLE.test(path)) setUndoReady(true);
    window.dispatchEvent(new CustomEvent(SAVED_EVENT, { detail: { path } }));
    const patched =
      s && (method ?? (body === undefined ? "GET" : "POST")) !== "GET"
        ? applyInPlace(s, path, value)
        : null;
    const entry = /^\/counts\/([^/]+)\/observations$/.exec(path);
    if (patched && entry) {
      setS(patched);
      cacheWorkspace(patched).catch(() => undefined);
      countEntrySaved(entry[1], (fn) => setS((prev) => fn(prev)));
    } else if (patched) {
      setS(patched);
      cacheWorkspace(patched).catch(() => undefined);
      invalidate();
    } else {
      await reload();
      invalidate();
    }
    return value;
  };
  /** Switches to another company: a fresh server session, nothing of this one is kept on screen. */
  async function switchCompany(companyId: string) {
    if (s && (await pending(scopeOf(s))).length) {
      notify(
        "Some count entries have not been sent yet. Tap Sync now, then switch companies.",
        true,
      );
      return;
    }
    try {
      await api("/auth/switch", { companyId });
      setCurrentVenue("");
      setModal(null);
      setPage("today");
      await reload();
      invalidate();
    } catch (e) {
      notify((e as Error).message, true);
    }
  }
  /** Changes the bar this tab works in (the venue is kept in the address bar). */
  async function switchVenue(venueId: string) {
    setCurrentVenue(venueId);
    setNavOpen(false);
    try {
      await reload();
      invalidate();
      window.scrollTo({ top: 0 });
    } catch (e) {
      notify((e as Error).message, true);
    }
  }
  /** Every Sign out control lands here (signOut.ts): warn about unsent entries, end the session, back to sign-in. */
  async function logout(anyway = false) {
    setProfileMenu(false);
    try {
      const result = await signOut(
        {
          unsynced: async () => (s ? (await pending(scopeOf(s))).length : 0),
          endSession: async () => {
            await api("/auth/logout", {});
          },
          clearCache: clearClientCache,
          clearOffline,
        },
        { anyway },
      );
      if (result.status === "needs-confirmation") {
        setModal(
          <Modal title="Sign out?" onClose={() => setModal(null)}>
            <p>{result.message}</p>
            <div className="sign-out-actions">
              <button
                className="button"
                onClick={async () => {
                  setModal(null);
                  if (!s) return;
                  const done = await syncPending(s);
                  await reload();
                  notify(`${done} ${done === 1 ? "entry" : "entries"} sent.`);
                }}
              >
                <RefreshCw size={15} /> Sync first
              </button>
              <button
                className="button secondary"
                onClick={() => {
                  setModal(null);
                  void logout(true);
                }}
              >
                <LogOut size={15} /> Sign out anyway
              </button>
            </div>
          </Modal>,
        );
        return;
      }
      setModal(null);
      setNavOpen(false);
      setS(null);
      setPage("today");
      // The next sign-in may be another company: its bar is not this one.
      setCurrentVenue("");
    } catch (e) {
      notify((e as Error).message, true);
    }
  }
  if (loading)
    return (
      <div className="boot">
        <Mark label="Parlyst" />
        <h2>Opening your workspace</h2>
        <LoaderCircle className="spin" />
      </div>
    );
  if (!s)
    return (
      <>
        {loadError && (
          <div className="connection-error">
            {loadError} Check your connection.{" "}
            <button onClick={() => location.reload()}>Try again</button>
          </div>
        )}
        <Suspense fallback={<PageLoading />}>
          <Auth onReady={reload} />
        </Suspense>
      </>
    );
  const p: PageProps = {
    s,
    reload,
    go,
    notify,
    modal: setModal,
    close: () => setModal(null),
    mutate,
    signOut: () => void logout(),
    switchCompany,
  };
  const canManage = s.role === "owner" || s.role === "manager";
  const allowedFeatures = featurePages.filter(
    (f) =>
      f.roles.includes(s.role) &&
      (!f.visible || f.visible(s)) &&
      pageAllowed(s, f.id),
  );
  const activeFeature = allowedFeatures.find((f) => f.id === page);
  // The grouped menu (nav/menu.ts): the same pages for the sidebar, the phone drawer and search.
  const menu = buildMenu(s);
  const openable = new Set(menu.flatMap((g) => g.items.map((i) => i.id)));
  const menuHasWork = Object.keys(navBadges(s)).length > 0 || s.openActions > 0;
  const multiVenue = !!s.venues && s.venues.length > 1;
  return (
    <div
      className={`app-shell${s.staffMode?.simpleMode ? " simple-mode" : ""}${!s.support ? " has-concierge" : ""}`}
      style={
        { "--accent": s.workspace.accent || "#243e25" } as React.CSSProperties
      }
    >
      {navOpen && (
        <div className="nav-scrim" onClick={() => setNavOpen(false)} />
      )}
      <aside
        id="app-menu"
        className={`sidebar ${navOpen ? "open" : ""}`}
        aria-label="Menu"
      >
        <div className="sidebar-top">
          <a
            className="brand"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              go("today");
            }}
          >
            <Wordmark />
          </a>
          <button
            ref={drawerClose}
            className="icon-button nav-close"
            aria-label="Close menu"
            onClick={() => setNavOpen(false)}
          >
            <X size={20} />
          </button>
        </div>
        <div className="sidebar-scroll">
          <SideNav s={s} page={page} go={go} menu={menu} />
          <div className="service-promo">
            <div className="promo-icon">
              <Leaf size={19} />
            </div>
            <strong>A little help behind the bar.</strong>
            <p>Let our team take the count.</p>
            <button onClick={() => go("service")}>
              Explore inventory service <ArrowUpRight size={15} />
            </button>
          </div>
        </div>
        <div className="sidebar-bottom">
          <button
            className="workspace-switch"
            title={
              (s.companies?.length ?? 0) > 1
                ? "Switch company"
                : "Company settings"
            }
            onClick={() =>
              (s.companies?.length ?? 0) > 1
                ? setModal(
                    <CompanySwitcher
                      s={s}
                      onClose={() => setModal(null)}
                      onSwitched={switchCompany}
                    />,
                  )
                : go("settings")
            }
          >
            <span className="workspace-avatar">
              {(s.workspace.name || s.venue?.name || "").slice(0, 1)}
            </span>
            <span>
              <strong>{s.workspace.name}</strong>
              <small>
                {s.company?.readableId ?? `${s.workspace.city}, NC`}
              </small>
            </span>
            <ChevronDown size={15} />
          </button>
          <div className="profile-row">
            {profileMenu && (
              <div
                className="profile-menu"
                role="menu"
                aria-label="Your account"
                onKeyDown={(e) => {
                  if (e.key === "Escape") setProfileMenu(false);
                }}
              >
                <button
                  role="menuitem"
                  onClick={() => {
                    setProfileMenu(false);
                    go("account");
                  }}
                >
                  <UserRound size={15} /> Your account
                </button>
                <button role="menuitem" onClick={() => void logout()}>
                  <LogOut size={15} /> Sign out
                </button>
              </div>
            )}
            <button
              className="profile"
              aria-haspopup="menu"
              aria-expanded={profileMenu}
              onClick={() => setProfileMenu(!profileMenu)}
            >
              <span className="user-avatar">
                {s.user.name
                  ?.split(" ")
                  .map((x: string) => x[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <span>
                <strong>{s.user.name}</strong>
                <small>
                  {s.role} ·{" "}
                  {s.workspace.isDemo ? "Demo workspace" : s.workspace.isTest ? "Test company" : "Private workspace"}
                </small>
              </span>
              <ChevronDown size={15} />
            </button>
          </div>
        </div>
      </aside>
      <div className="main-wrap">
        <header className="topbar">
          <button
            ref={menuButton}
            className="icon-button mobile-menu"
            onClick={() => setNavOpen(!navOpen)}
            aria-label="Open menu"
            aria-expanded={navOpen}
            aria-controls="app-menu"
          >
            <Menu size={21} />
            {menuHasWork && <i className="notification-dot" />}
          </button>
          <Mark label="Parlyst" />
          <div className={`breadcrumb${multiVenue ? " has-venues" : ""}`}>
            {multiVenue ? (
              <label
                className="venue-switch"
                title="Which bar you are working in"
              >
                <Store size={15} />
                <select
                  value={s.venue.id}
                  onChange={(e) => switchVenue(e.target.value)}
                  aria-label="Venue"
                >
                  {s.venues
                    .filter((v) => !v.archivedAt || v.id === s.venue.id)
                    .map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                        {v.archivedAt ? " (archived)" : ""}
                      </option>
                    ))}
                </select>
              </label>
            ) : (
              <span className="crumb-root">Workspace</span>
            )}
            <span className="crumb-sep" aria-hidden="true">
              /
            </span>
            <strong className="crumb-title">{pageTitle(s, page)}</strong>
          </div>
          <div className="topbar-right">
            <GlobalSearch s={s} go={go} pages={searchablePages(menu)} />
            {s.support && <Badge tone="orange">SUPPORT VIEW</Badge>}
            {s.workspace.isDemo && <Badge tone="sand">SAMPLE BAR</Badge>}
            {s.workspace.isTest && <Badge tone="orange">Test company</Badge>}
            <span className="live-status">
              <i />
              {offline ? "Offline" : "Workspace synced"}
            </span>
            <button
              className="icon-button"
              aria-label="How this page works"
              title="How this page works"
              onClick={() =>
                setModal(
                  <Suspense fallback={null}>
                  <HelpDrawer
                    pageId={activeFeature?.id ?? page}
                    pageTitle={pageTitle(s, page)}
                    onClose={() => setModal(null)}
                    basics={
                    <div className="help-steps">
                      <p>
                        <b>1. Set up your bar.</b> Add the places you keep
                        stock. Add your products. Check bottle sizes.
                      </p>
                      <p>
                        <b>2. Count each area.</b> Scan or search. Count the
                        sealed bottles, then the open ones. Anything you skip
                        stays "not counted", never zero.
                      </p>
                      <p>
                        <b>3. Check and finish.</b> Answer anything still
                        missing. A manager finishes (finalizes) the count. It
                        becomes your starting point.
                      </p>
                      <p>
                        <b>4. Keep it current.</b> Record deliveries, waste and
                        moves. Bring in your sales and link each register button
                        to a recipe.
                      </p>
                      <p>
                        Need to leave? Everything you saved is kept. Entries
                        saved while offline stay on this device and send
                        themselves when you are back online.
                      </p>
                      <p>
                        <button
                          className="text-button"
                          onClick={() => {
                            setModal(null);
                            go("help");
                          }}
                        >
                          What do the words mean? Open the glossary{" "}
                          <ArrowRight size={14} />
                        </button>
                      </p>
                    </div>
                    }
                  />
                  </Suspense>,
                )
              }
            >
              <HelpCircle size={19} />
            </button>
            <button
              className="icon-button"
              aria-label="View open actions"
              onClick={() => go("today")}
            >
              <Bell size={19} />
              {s.openActions > 0 && <i className="notification-dot" />}
            </button>
            <button
              className="icon-button"
              aria-label="Sign out"
              title="Sign out"
              onClick={() => void logout()}
            >
              <LogOut size={18} />
            </button>
          </div>
        </header>
        {s.support && (
          <div className="offline-banner support-banner">
            <Eye size={16} />
            Read-only support view of {s.workspace.name} (
            {s.company?.readableId}) until{" "}
            {new Date(s.support.expiresAt).toLocaleString()}. Every screen you
            open is logged for the owner.
            {s.companies?.length ? (
              <button onClick={() => switchCompany(s.companies[0].id)}>
                <LogOut size={14} /> Leave support view
              </button>
            ) : null}
          </div>
        )}
        {s.accountAccess &&
          (s.accountAccess as { mode?: string }).mode !== "full" &&
          !s.support && (
            <Suspense fallback={null}>
              <AccessBanner s={s} go={go} reload={reload} notify={notify} />
            </Suspense>
          )}
        {(offline || queued > 0) && (
          <div className="offline-banner">
            <WifiOff size={16} />
            {offline
              ? "You are offline. You see the last data this device loaded. Count entries are kept here until you are back online."
              : `${queued} count ${queued === 1 ? "entry is" : "entries are"} waiting to send.`}
            <button
              onClick={async () => {
                const done = await syncPending(s);
                await reload();
                notify(`${done} ${done === 1 ? "entry" : "entries"} sent.`);
              }}
            >
              <RefreshCw size={14} /> Sync now
            </button>
          </div>
        )}
        <main>
          {/* A screen that fails shows a panel (and reports it); the navigation keeps working. */}
          <ScreenErrorBoundary
            page={activeFeature?.id ?? page}
            resetKey={activeFeature?.id ?? page}
            onToday={() => go("today")}
          >
            <Suspense fallback={<PageLoading />}>
              {activeFeature ? (
                <activeFeature.component {...p} />
              ) : page === "today" ? (
                s.staffMode?.simpleMode ? (
                  <StaffHome {...p} />
                ) : (
                  <Today {...p} />
                )
              ) : page === "inventory" ? (
                <Inventory {...p} />
              ) : page === "counts" ? (
                <Counts
                  {...p}
                  onQueued={async () =>
                    setQueued(
                      (await pending(scopeOf(s)).catch(() => [])).length,
                    )
                  }
                />
              ) : page === "purchasing" ? (
                <Purchasing {...p} />
              ) : page === "recipes" ? (
                <Recipes {...p} />
              ) : page === "reports" ? (
                <Reports {...p} />
              ) : page === "connections" ? (
                <Connections {...p} />
              ) : page === "service" ? (
                <Service {...p} />
              ) : page === "team" ? (
                <Team {...p} />
              ) : (
                <SettingsPage {...p} />
              )}
            </Suspense>
          </ScreenErrorBoundary>
        </main>
        <footer className="app-footer">
          <span>
            <Mark /> Clarity, by the bottle.
          </span>
          <span>
            {s.venue?.name ?? s.workspace.venueName} ·{" "}
            {s.venue?.readableId ?? ""} ·{" "}
            {s.venue?.timezone || s.workspace.timezone || "America/New_York"}
          </span>
        </footer>
      </div>
      {modal}
      {!s.support && (
        <Suspense fallback={null}>
          <SamLauncher key={s.user.id} page={page} go={go} />
        </Suspense>
      )}
      {undoReady && (
        <Suspense fallback={null}>
          <UndoBar p={p} lookNow />
        </Suspense>
      )}
      <Toast toast={toast} onClose={() => setToast(null)} />
      {/* "?" shortcuts overlay and the right-side record panel (openRecord). */}
      <ShortcutsHost go={go} canOpen={(id) => openable.has(id)} />
      <RecordHost s={s} go={go} canOpenPage={(id) => openable.has(id)} />
    </div>
  );
}
function CompanySwitcher({
  s,
  onClose,
  onSwitched,
}: {
  s: Snapshot;
  onClose: () => void;
  onSwitched: (id: string) => Promise<void>;
}) {
  return (
    <Modal
      title="Switch company"
      subtitle="You belong to more than one company. Each one opens on its own; nothing is mixed."
      onClose={onClose}
    >
      <div className="company-list">
        {s.companies.map((c) => (
          <button
            key={c.id}
            className={`company-row ${c.id === s.workspace.id ? "current" : ""}`}
            disabled={c.id === s.workspace.id}
            onClick={() => onSwitched(c.id)}
          >
            <Building2 size={18} />
            <span>
              <strong>{c.name || "Unnamed company"}</strong>
              <small>
                {c.readableId ?? ""} · {c.role}
                {c.isDemo ? " · sample" : ""}
              </small>
            </span>
            {c.id === s.workspace.id ? (
              <Badge tone="green">Current</Badge>
            ) : (
              <ArrowRight size={16} />
            )}
          </button>
        ))}
      </div>
    </Modal>
  );
}
/** The page named in the address (?page=…), e.g. after a POS provider sends the browser back. */
function initialPage() {
  try {
    const page = new URL(location.href).searchParams.get("page");
    return page && /^[a-z-]+$/.test(page) ? page : "today";
  } catch {
    return "today";
  }
}
